import { useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import {
  isPivotSheet,
  type WorkbookDefinition,
  type WorkbookSheet,
} from '@erp/shared-types';
import { pivotStaticColumns, resultColumns } from '@erp/workbooks';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import { agentWorksheetResult } from './workbookAgentReads';
import { targetSheet } from './workbookAgentPreview';
import {
  createStatefulTableInputSchema,
  statefulTablePlan,
  type StatefulTableSheet,
} from './workbookStatefulTables';

type StatefulTableDefinition = Awaited<
  ReturnType<typeof rpcClient.statefulTables.get>
>['definition'];

function agentTable(definition: StatefulTableDefinition) {
  return {
    id: definition.id,
    name: definition.name,
    columns: definition.currentVersion.columns.map(({ name, type }) => ({
      name,
      type,
    })),
    keyColumns: definition.currentVersion.keyColumns,
  };
}

/**
 * A Worksheet's columns with their Declared types. A pivot whose Column keys
 * come from its data has columns only a Result knows, so they are read from
 * its current one.
 */
async function sheetColumns(
  workbookId: string,
  port: WorkbookAgentPort,
  definition: WorkbookDefinition,
  sheet: WorkbookSheet,
): Promise<StatefulTableSheet['columns']> {
  if (!isPivotSheet(sheet) || !pivotStaticColumns(sheet.pivot).dynamic)
    return port.plan(definition).tables.get(sheet.id) ?? [];
  const read = agentWorksheetResult(
    await rpcClient.workbooks.runs({ workbookId }),
    definition,
    sheet.id,
    0,
  );
  if (!read?.current)
    throw new Error(
      `The ${sheet.name} pivot takes its columns from its data and has no current result: run it first, or pin its column keys.`,
    );
  return resultColumns(sheet, read.columns);
}

/**
 * The on-page agent creates a Stateful table for a sink output to write,
 * typically shaped like a Worksheet. Names aren't unique on the server, so the
 * tool refuses a name already taken rather than making a second table or
 * reusing one another writer may own.
 */
export function useWorkbookAgentStatefulTools(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  // statefulTables.create checks Create on Workflow; the same-name lookup (statefulTables.list) checks Read.
  const [canCreate] = useCan(Action.Create, Subject.Workflow);
  const [canRead] = useCan(Action.Read, Subject.Workflow);
  const queryClient = useQueryClient();
  return useFrontendTool({
    name: 'create_stateful_table',
    enabled: canCreate && canRead,
    description:
      "Create a Stateful table for a sink output to write, by default with a worksheet's columns (named in lower snake case, typed from their declared types). Returns the table (id, columns, keyColumns) and the mappings a sink output from that worksheet uses. When a table of that name already exists the call fails and names it: pick another name.",
    parameters: createStatefulTableInputSchema,
    awaitable: true,
    execute: async (input) => {
      const { definition } = port.snapshot();
      const sheet = input.sheet ? targetSheet(definition, input.sheet) : null;
      const plan = statefulTablePlan(
        input,
        sheet && {
          name: sheet.name,
          columns: await sheetColumns(workbookId, port, definition, sheet),
        },
      );
      const {
        results: [match],
      } = await rpcClient.statefulTables.list({ name: input.name, perPage: 1 });
      if (match) {
        // Reusing it could merge, overwrite or blank rows another writer owns.
        const existing = agentTable(match);
        throw new Error(
          `A Stateful table named ${existing.name} already exists (id ${existing.id}, keyed on ${existing.keyColumns.join(', ')}). Pick another name, or add a sink output to that table yourself after checking its columns.`,
        );
      }
      const { definition: created } = await rpcClient.statefulTables.create({
        name: input.name,
        description: input.description,
        columns: plan.columns,
        keyColumns: plan.keyColumns,
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.workbooks.sinkTargets.key(),
      });
      return {
        table: agentTable(created),
        mappings: plan.mappings,
      };
    },
  });
}
